import {
  type MotionValue,
  m,
  useMotionValue,
  useTransform,
  transform,
} from 'motion/react'
import { useCallback, useEffect } from 'react'
import { useElementSize, type ElementSize } from '@/hooks/element-hooks'
import styles from './MagicBorder.module.css'
import cx from 'classnames'

export interface MagicBorderProps {
  progress: MotionValue<number>
  contentHeight: MotionValue<number>
  contentWidth: MotionValue<number>
  offset?: number
  position?: 'sticky' | 'static'
}

/**
 * A magical border that expands and contracts based on a progress motion value.
 * Used as a cue to visually anchor a sticky header such as a tab bar or
 * a table header to the sides of the scroll port.
 */
export function MagicBorder({
  progress: animationProgress,
  contentHeight,
  contentWidth,
  offset: offsetProp = 0,
  position = 'sticky',
}: MagicBorderProps) {
  const borderWidth = useMotionValue(0)
  const { connectRef } = useElementSize({
    onChange: useCallback(
      (s: ElementSize) => borderWidth.set(s.width),
      [borderWidth],
    ),
  })

  const maskImage = useTransform(() => {
    const innerWidth = contentWidth.get()
    const outerWidth = borderWidth.get()

    const spacing = (outerWidth - innerWidth) / 2
    const fadeWidth = 20
    const leftWhiteStart = spacing + fadeWidth
    const leftTransparentStart = leftWhiteStart
    const rightWhiteStart = spacing + innerWidth - fadeWidth
    const rightTransparentStart = rightWhiteStart

    const leftWhiteEnd = 0
    const leftTransparentEnd = 0
    const rightWhiteEnd = outerWidth
    const rightTransparentEnd = outerWidth

    const progress = animationProgress.get()

    if (progress === 0) {
      return 'linear-gradient(to right, transparent, transparent)'
    }

    return `
      linear-gradient(
        to right,
        transparent,
        transparent ${transform(progress, [0, 1], [leftTransparentStart, leftTransparentEnd])}px,
        white ${transform(progress, [0, 1], [leftWhiteStart, leftWhiteEnd])}px,
        white ${innerWidth}px,
        white ${transform(progress, [0, 1], [rightWhiteStart, rightWhiteEnd])}px,
        transparent ${transform(progress, [0, 1], [rightTransparentStart, rightTransparentEnd])}px,
        transparent
      )`
  })

  const offset = useMotionValue(0)
  useEffect(() => offset.set(offsetProp), [offset, offsetProp])
  const marginBottom = useTransform(() => -contentHeight.get())
  const top = useTransform(() => offset.get() + contentHeight.get() - 1)

  return (
    <m.div
      aria-hidden
      className={cx(styles.root, position === 'static' && styles.static)}
      ref={connectRef}
      style={{
        marginBottom: position === 'sticky' ? marginBottom : undefined,
        top: position === 'sticky' ? top : undefined,
        maskImage,
        marginTop: position === 'sticky' ? contentHeight : undefined,
      }}
    />
  )
}
